import {
    faCalendar,
    faCircle,
    faCircleCheck,
    faCommentDots,
    faFilePen,
    faListCheck,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import TriStateToggle, { TriStateToggleValue } from '../TriStateToggle'
import ToggleSwitch3 from '../ToggleSwitch3'
import Button from '../Button'
import {
    CandidateLiteDto,
    InterestedValues,
    PresentCandidateValues,
} from '../../api/role/models/CandidateDto'
import { toast } from 'sonner'
import { useManageRoleStore } from './stateStore/manageRoleStore'
import { safeCastToEnum } from '../../utils/enumUtils'
import roleApi from '../../api/role/RoleApi'
import LoadingSpinner from '../LoadingSpinner'
import BookMeetingModal from './BookMeetingModal'

type Props = {
    candidate: CandidateLiteDto
}

export default function CandidateControls({
    candidate,
}: Props): React.JSX.Element {
    // from shared store
    const roleLite = useManageRoleStore((state) => state.roleLite)
    const updatePresentForCandidate = useManageRoleStore(
        (state) => state.updatePresentForCandidate,
    )
    const updateInterestedForCandidate = useManageRoleStore(
        (state) => state.updateInterestedForCandidate,
    )
    const updateContactedForCandidate = useManageRoleStore(
        (state) => state.updateContactedForCandidate,
    )

    const [shortlisted, setShortlisted] =
        useState<TriStateToggleValue>('not_selected')
    const [interested, setInterested] =
        useState<TriStateToggleValue>('not_selected')
    const [contacted, setContacted] = useState(false)

    const [showApplyButton, setShowApplyButton] = useState(false)
    const [showBookButton, setShowBookButton] = useState(false)
    const [applyLoading, setApplyLoading] = useState(false)
    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)

    useEffect(() => {
        if (candidate.present === 'yes') {
            setShortlisted('yes')
        } else if (candidate.present === 'no') {
            setShortlisted('no')
        } else {
            setShortlisted('not_selected')
        }

        if (candidate.interested === 'yes') {
            setInterested('yes')
        } else if (candidate.interested === 'no') {
            setInterested('no')
        } else {
            setInterested('not_selected')
        }

        if (candidate.contacted) {
            setContacted(true)
        } else {
            setContacted(false)
        }

        setShowApplyButton(false)
        setShowBookButton(false)

        if (!candidate.contacted && !candidate.interested) {
            setShowApplyButton(true)
        } else {
            if (candidate.interested === InterestedValues.YES) {
                setShowBookButton(true)
            }
        }
    }, [candidate])

    async function handleShortlistedChange(value: TriStateToggleValue) {
        const origValue = shortlisted
        setShortlisted(value)

        try {
            await updatePresentForCandidate(
                candidate.candidatePersonId,
                value === 'not_selected'
                    ? undefined
                    : safeCastToEnum(value, PresentCandidateValues),
            )
        } catch (error) {
            setShortlisted(origValue)
            console.error(error)
            toast.error(
                `There was a problem updating the candidate, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function handleInterestedChange(value: TriStateToggleValue) {
        const origValue = interested
        setInterested(value)

        try {
            await updateInterestedForCandidate(
                candidate.candidatePersonId,
                value === 'not_selected'
                    ? undefined
                    : safeCastToEnum(value, InterestedValues),
            )
        } catch (error) {
            setInterested(origValue)
            console.error(error)
            toast.error(
                `There was a problem updating the candidate, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function handleContactedChange(value: boolean) {
        const origValue = contacted
        setContacted(value)

        try {
            await updateContactedForCandidate(
                candidate.candidatePersonId,
                value,
            )
        } catch (error) {
            setContacted(origValue)
            console.error(error)
            toast.error(
                `There was a problem updating the candidate, please try again.`,
                {
                    duration: 5000,
                },
            )
        }
    }

    async function handleInviteToApplyClick() {
        if (!roleLite) {
            return
        }

        setApplyLoading(true)

        try {
            await roleApi.inviteCandidateToApply(
                roleLite.id,
                candidate.candidatePersonId,
            )

            await updateContactedForCandidate(
                candidate.candidatePersonId,
                true,
                true,
            )

            toast.message(
                <div>
                    <FontAwesomeIcon
                        icon={faCircleCheck}
                        className="mr-2 text-green-500"
                    />
                    The candidate has been invited to apply.
                </div>,
                {
                    duration: 5000,
                },
            )
        } catch (error) {
            console.error(error)
            toast.error(
                `There was a problem contacting the candidate, please try again.`,
                {
                    duration: 5000,
                },
            )
        } finally {
            setApplyLoading(false)
        }
    }

    return (
        <div>
            <div className="flex justify-between items-start">
                <div className="flex gap-8">
                    <div>
                        <div className="text-sm mb-2 text-gray-700">
                            <FontAwesomeIcon
                                icon={faListCheck}
                                className="mr-2"
                            />
                            <span className="font-bold">Shortlisted</span>
                        </div>
                        <TriStateToggle
                            value={shortlisted}
                            onChange={handleShortlistedChange}
                        />
                    </div>
                    <div>
                        <div className="text-sm mb-2 text-gray-700">
                            <FontAwesomeIcon
                                icon={faCommentDots}
                                className="mr-2"
                            />
                            <span className="text-sm font-bold">Contacted</span>
                        </div>
                        <ToggleSwitch3
                            value={contacted}
                            onChange={handleContactedChange}
                        />
                    </div>
                    <div>
                        <div className="text-sm mb-2 text-gray-700">
                            <FontAwesomeIcon icon={faCircle} className="mr-2" />
                            <span className="text-sm font-bold">
                                Interested
                            </span>
                        </div>
                        <TriStateToggle
                            value={interested}
                            onChange={handleInterestedChange}
                        />
                    </div>
                </div>
                {showApplyButton && (
                    <div className="">
                        <Button
                            type="button"
                            style="primary"
                            onClick={handleInviteToApplyClick}
                            size="small"
                            enabled={!applyLoading}
                        >
                            {applyLoading && (
                                <LoadingSpinner spinnerSize={24} />
                            )}
                            {!applyLoading && (
                                <FontAwesomeIcon icon={faFilePen} />
                            )}
                            <div className="ml-2">Invite to Apply</div>
                        </Button>
                    </div>
                )}
                {showBookButton && (
                    <div className="">
                        <Button
                            type="button"
                            style="primary"
                            onClick={() => {
                                setIsBookingModalOpen(true)
                            }}
                            size="small"
                        >
                            <FontAwesomeIcon
                                icon={faCalendar}
                                className="mr-2"
                            />
                            Book Interview
                        </Button>
                    </div>
                )}
                {roleLite && isBookingModalOpen && (
                    <BookMeetingModal
                        roleId={roleLite.id}
                        candidate={candidate}
                        onClose={() => {
                            setIsBookingModalOpen(false)
                        }}
                    />
                )}
            </div>
        </div>
    )
}
